import { Fragment, useEffect, useMemo, useState, useCallback } from 'react';

function BanquetCrmConfig({ token, selectedProperty, selectedOutletId = '' }) {
  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const outletId = String(selectedOutletId || '').trim();
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      'x-chain-id': chainId,
      'x-property-id': propertyId,
      ...(outletId ? { 'x-outlet-id': outletId } : {})
    }),
    [token, chainId, propertyId, outletId]
  );

  const [activeTab, setActiveTab] = useState('config');
  const [config, setConfig] = useState(null);
  const [pipelineStages, setPipelineStages] = useState([]);
  const [lostReasons, setLostReasons] = useState([]);
  const [scoringRules, setScoringRules] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // EaseCRM Integration state
  const [crmStatusUrl, setCrmStatusUrl] = useState('');
  const [crmSecret, setCrmSecret] = useState('');
  const [crmSecretMasked, setCrmSecretMasked] = useState('');
  const [crmSecretSet, setCrmSecretSet] = useState(false);
  const [crmSecretVisible, setCrmSecretVisible] = useState(false);
  const [crmSaving, setCrmSaving] = useState(false);
  const [crmMessage, setCrmMessage] = useState('');
  const [crmError, setCrmError] = useState('');
  const [crmTesting, setCrmTesting] = useState(false);
  const [crmTestResult, setCrmTestResult] = useState(null);
  const [crmUrlCopied, setCrmUrlCopied] = useState(false);
  const crmLeadCallbackUrl = `${window.location.origin}/api/v1/banquet-crm/lead-callback`;

  // EaseCRM Per-Property Bearer-token Integration (newer endpoint set)
  const [ecrmCfg, setEcrmCfg] = useState({
    is_enabled: true,
    lead_push_url: 'https://easecrm.easeroom.co/api/lead_push',
    lead_push_token: '',
    lead_push_token_masked: '',
    lead_push_token_set: false,
    lead_stage_update_url: 'https://easecrm.easeroom.co/api/lead_stage_update',
    lead_stage_update_token: '',
    lead_stage_update_token_masked: '',
    lead_stage_update_token_set: false,
    default_source_id: '',
    default_source_name: '',
    default_sub_source_id: '',
    default_sub_source_name: ''
  });
  const [ecrmSources, setEcrmSources] = useState([]);
  const [ecrmSubSources, setEcrmSubSources] = useState([]);
  const [ecrmLoadingSources, setEcrmLoadingSources] = useState(false);
  const [ecrmLoadingSubSources, setEcrmLoadingSubSources] = useState(false);
  const [ecrmSaving, setEcrmSaving] = useState(false);
  const [ecrmMessage, setEcrmMessage] = useState('');
  const [ecrmError, setEcrmError] = useState('');

  // Push Log viewer
  const [pushLog, setPushLog] = useState([]);
  const [pushLogLoading, setPushLogLoading] = useState(false);
  const [pushLogError, setPushLogError] = useState('');
  const [pushLogFilterLead, setPushLogFilterLead] = useState('');
  const [pushLogFilterOutcome, setPushLogFilterOutcome] = useState('');

  // Modals
  const [showStageModal, setShowStageModal] = useState(false);
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // Forms
  const [stageForm, setStageForm] = useState({ stage_key: '', stage_name: '', stage_color: '#3b82f6', sort_order: 0, is_active: true, sla_hours: 0, is_terminal: false, auto_action: '', crm_stage_id: '' });
  const [reasonForm, setReasonForm] = useState({ reason_key: '', reason_label: '', is_active: true });
  const [ruleForm, setRuleForm] = useState({ score_dimension: '', rule_field: '', rule_operator: '', rule_value: '', score_points: 0, is_active: true });
  const [configForm, setConfigForm] = useState({
    auto_assign_enabled: false,
    auto_assign_user_ids: '',
    auto_acknowledge_enabled: false,
    sla_new_lead_hours: 4,
    sla_proposal_hours: 72,
    stale_lead_days: 14,
    tentative_hold_days: 7,
    scoring_weights: '{"conversion":40,"revenue":35,"urgency":25}',
    bucket_thresholds: '{"VIP":80,"HOT":60,"WARM":40,"COLD":20}',
    feedback_auto_send_days: 1
  });

  const parseJson = async (res) => {
    const text = await res.text();
    try {
      const json = text ? JSON.parse(text) : {};
      return { data: json, error: json?.error || '' };
    } catch {
      return { data: null, error: text || '' };
    }
  };

  const loadConfig = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/config', { headers });
      const { data } = await parseJson(res);
      if (res.ok && data) {
        setConfig(data);
        setConfigForm({
          auto_assign_enabled: data.auto_assign_enabled || false,
          auto_assign_user_ids: data.auto_assign_user_ids || '',
          auto_acknowledge_enabled: data.auto_acknowledge_enabled || false,
          sla_new_lead_hours: data.sla_new_lead_hours ?? 4,
          sla_proposal_hours: data.sla_proposal_hours ?? 72,
          stale_lead_days: data.stale_lead_days ?? 14,
          tentative_hold_days: data.tentative_hold_days ?? 7,
          scoring_weights: typeof data.scoring_weights === 'object' ? JSON.stringify(data.scoring_weights) : (data.scoring_weights || '{"conversion":40,"revenue":35,"urgency":25}'),
          bucket_thresholds: typeof data.bucket_thresholds === 'object' ? JSON.stringify(data.bucket_thresholds) : (data.bucket_thresholds || '{"VIP":80,"HOT":60,"WARM":40,"COLD":20}'),
          feedback_auto_send_days: data.feedback_auto_send_days ?? 1
        });
      }
    } catch {}
  };

  const loadPipelineStages = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/pipeline-stages', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setPipelineStages(data.stages || []);
    } catch {}
  };

  const loadLostReasons = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/lost-reasons', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setLostReasons(data.reasons || []);
    } catch {}
  };

  const loadScoringRules = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/scoring-rules', { headers });
      const { data } = await parseJson(res);
      if (res.ok) setScoringRules(data.rules || []);
    } catch {}
  };

  // ─── EaseCRM integration helpers ────────────────────────────────────
  const adminHeaders = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });
  const isValidUrl = (val) => { try { new URL(val.trim()); return true; } catch { return false; } };

  const loadCrmConfig = async () => {
    try {
      const res = await fetch('/api/admin/channel-manager', { headers: adminHeaders() });
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setCrmStatusUrl(data.crm_status_update_url || '');
          setCrmSecretSet(Boolean(data.crm_shared_secret_set));
          setCrmSecretMasked(data.crm_shared_secret_masked || '');
        }
      }
    } catch {}
  };

  const handleCopyCrmUrl = () => {
    navigator.clipboard.writeText(crmLeadCallbackUrl).then(() => {
      setCrmUrlCopied(true);
      setTimeout(() => setCrmUrlCopied(false), 2000);
    });
  };

  const handleGenerateCrmSecret = () => {
    const key = Array.from(crypto.getRandomValues(new Uint8Array(32))).map(b => b.toString(16).padStart(2, '0')).join('');
    setCrmSecret(key);
    setCrmSecretVisible(true);
  };

  const handleSaveCrm = async () => {
    setCrmError(''); setCrmMessage(''); setCrmTestResult(null);
    if (crmStatusUrl.trim() && !isValidUrl(crmStatusUrl)) { setCrmError('CRM Status Update URL must be a valid URL'); return; }
    setCrmSaving(true);
    try {
      // GET current CM config to preserve existing fields
      const getRes = await fetch('/api/admin/channel-manager', { headers: adminHeaders() });
      const current = getRes.ok ? await getRes.json() : {};
      const body = {
        channel_manager_name: current?.channel_manager_name || 'EASEROOM',
        api_url: current?.api_url || 'http://placeholder',
        crm_status_update_url: crmStatusUrl.trim() || null
      };
      if (crmSecret) body.crm_shared_secret = crmSecret;
      const res = await fetch('/api/admin/channel-manager', {
        method: 'POST', headers: adminHeaders(), body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) { setCrmError(data?.error || 'Failed to save'); return; }
      setCrmMessage('EaseCRM settings saved.');
      if (data.crm_shared_secret_set) {
        setCrmSecretSet(true);
        setCrmSecretMasked(data.crm_shared_secret_masked || '');
        setCrmSecret('');
        setCrmSecretVisible(false);
      }
    } catch { setCrmError('Network error'); }
    finally { setCrmSaving(false); }
  };

  const handleTestCrmConnection = async () => {
    setCrmError(''); setCrmMessage(''); setCrmTestResult(null); setCrmTesting(true);
    try {
      const res = await fetch('/api/admin/channel-manager/test-crm-connection', {
        method: 'POST', headers: adminHeaders()
      });
      const data = await res.json();
      if (!res.ok) { setCrmError(data?.error || 'Test failed'); return; }
      setCrmTestResult(data);
    } catch { setCrmError('Network error'); }
    finally { setCrmTesting(false); }
  };

  // ─── EaseCRM Per-Property (Bearer-token) handlers ──────────────────────
  const loadEcrmConfig = async () => {
    try {
      const res = await fetch('/api/v1/banquet-crm/easecrm/property-config', { headers });
      if (!res.ok) return; // 4xx is fine — no config row yet
      const data = await res.json();
      setEcrmCfg((prev) => ({
        ...prev,
        is_enabled: data.is_enabled !== false,
        lead_push_url: data.lead_push_url || prev.lead_push_url,
        lead_push_token: '',
        lead_push_token_masked: data.lead_push_token_masked || '',
        lead_push_token_set: !!data.lead_push_token_set,
        lead_stage_update_url: data.lead_stage_update_url || prev.lead_stage_update_url,
        lead_stage_update_token: '',
        lead_stage_update_token_masked: data.lead_stage_update_token_masked || '',
        lead_stage_update_token_set: !!data.lead_stage_update_token_set,
        default_source_id: data.default_source_id || '',
        default_source_name: data.default_source_name || '',
        default_sub_source_id: data.default_sub_source_id || '',
        default_sub_source_name: data.default_sub_source_name || ''
      }));
    } catch (_) { /* surface via UI on save */ }
  };

  const saveEcrmConfig = async () => {
    setEcrmError(''); setEcrmMessage(''); setEcrmSaving(true);
    try {
      const body = {
        is_enabled: ecrmCfg.is_enabled,
        lead_push_url: ecrmCfg.lead_push_url || null,
        lead_stage_update_url: ecrmCfg.lead_stage_update_url || null,
        default_source_id: ecrmCfg.default_source_id || null,
        default_source_name: ecrmCfg.default_source_name || null,
        default_sub_source_id: ecrmCfg.default_sub_source_id || null,
        default_sub_source_name: ecrmCfg.default_sub_source_name || null
      };
      // Tokens: only send if user typed a new value (omit to keep existing).
      if (ecrmCfg.lead_push_token) body.lead_push_token = ecrmCfg.lead_push_token;
      if (ecrmCfg.lead_stage_update_token) body.lead_stage_update_token = ecrmCfg.lead_stage_update_token;
      const res = await fetch('/api/v1/banquet-crm/easecrm/property-config', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setEcrmError(data?.error || 'Save failed'); return; }
      setEcrmMessage('Saved.');
      await loadEcrmConfig();
    } catch (err) { setEcrmError(err?.message || 'Network error'); }
    finally { setEcrmSaving(false); }
  };

  const fetchEcrmSources = async () => {
    setEcrmError(''); setEcrmLoadingSources(true);
    try {
      const res = await fetch('/api/v1/banquet-crm/easecrm/sources', { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setEcrmError(data?.error || 'Failed to fetch sources'); setEcrmSources([]); return; }
      // EaseCRM responds: { Status: 1, StatusDesc: "Success", LeadSources: [{ source_id, source_name }] }
      const list = Array.isArray(data) ? data
        : (Array.isArray(data?.LeadSources) ? data.LeadSources
          : (Array.isArray(data?.lead_sources) ? data.lead_sources
            : (Array.isArray(data?.sources) ? data.sources
              : (Array.isArray(data?.data) ? data.data : []))));
      setEcrmSources(list);
    } catch (err) { setEcrmError(err?.message || 'Network error'); setEcrmSources([]); }
    finally { setEcrmLoadingSources(false); }
  };

  const fetchEcrmSubSources = async (sourceId) => {
    if (!sourceId) { setEcrmSubSources([]); return; }
    setEcrmError(''); setEcrmLoadingSubSources(true);
    try {
      const res = await fetch(`/api/v1/banquet-crm/easecrm/sub-sources?source_id=${encodeURIComponent(sourceId)}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setEcrmError(data?.error || 'Failed to fetch sub-sources'); setEcrmSubSources([]); return; }
      // EaseCRM responds: { Status: 1, StatusDesc: "Success", LeadSubSources: [{ sub_source_id, sub_source_name }] }
      const list = Array.isArray(data) ? data
        : (Array.isArray(data?.LeadSubSources) ? data.LeadSubSources
          : (Array.isArray(data?.lead_sub_sources) ? data.lead_sub_sources
            : (Array.isArray(data?.sub_sources) ? data.sub_sources
              : (Array.isArray(data?.data) ? data.data : []))));
      setEcrmSubSources(list);
    } catch (err) { setEcrmError(err?.message || 'Network error'); setEcrmSubSources([]); }
    finally { setEcrmLoadingSubSources(false); }
  };

  const loadPushLog = async () => {
    setPushLogError(''); setPushLogLoading(true);
    try {
      const qs = new URLSearchParams({ limit: '50' });
      if (pushLogFilterLead.trim()) qs.set('lead_no', pushLogFilterLead.trim());
      if (pushLogFilterOutcome.trim()) qs.set('outcome', pushLogFilterOutcome.trim());
      const res = await fetch(`/api/v1/banquet-crm/easecrm/push-log?${qs.toString()}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setPushLogError(data?.error || 'Failed to load push log'); setPushLog([]); return; }
      setPushLog(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) { setPushLogError(err?.message || 'Network error'); setPushLog([]); }
    finally { setPushLogLoading(false); }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([loadConfig(), loadPipelineStages(), loadLostReasons(), loadScoringRules(), loadCrmConfig(), loadEcrmConfig()]);
    setLoading(false);
  }, [headers]);

  useEffect(() => {
    if (chainId && propertyId) loadAll();
  }, [chainId, propertyId]);

  const saveConfig = async () => {
    try {
      const payload = {
        ...configForm,
        scoring_weights: typeof configForm.scoring_weights === 'string' ? JSON.parse(configForm.scoring_weights) : configForm.scoring_weights,
        bucket_thresholds: typeof configForm.bucket_thresholds === 'string' ? JSON.parse(configForm.bucket_thresholds) : configForm.bucket_thresholds
      };
      const res = await fetch('/api/v1/banquet-crm/config', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to save config');
      }
      setMessage('Configuration saved');
      loadConfig();
    } catch (err) {
      setError(err?.message || 'Failed to save config');
    }
  };

  const saveStage = async () => {
    if (!stageForm.stage_key || !stageForm.stage_name) {
      setError('Stage key and name are required');
      return;
    }
    try {
      const res = await fetch('/api/v1/banquet-crm/pipeline-stages', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ stages: [stageForm] })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to save stage');
      }
      setMessage(editingItem ? 'Stage updated' : 'Stage created');
      setShowStageModal(false);
      setEditingItem(null);
      loadPipelineStages();
    } catch (err) {
      setError(err?.message || 'Failed to save stage');
    }
  };

  const saveLostReason = async () => {
    if (!reasonForm.reason_key || !reasonForm.reason_label) {
      setError('Reason key and label are required');
      return;
    }
    try {
      const res = await fetch('/api/v1/banquet-crm/lost-reasons', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ reasons: [reasonForm] })
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to save reason');
      }
      setMessage(editingItem ? 'Reason updated' : 'Reason created');
      setShowReasonModal(false);
      setEditingItem(null);
      loadLostReasons();
    } catch (err) {
      setError(err?.message || 'Failed to save reason');
    }
  };

  const saveScoringRule = async () => {
    if (!ruleForm.score_dimension || !ruleForm.rule_field || !ruleForm.rule_operator) {
      setError('Score dimension, rule field, and rule operator are required');
      return;
    }
    try {
      const payload = { ...ruleForm };
      if (editingItem) {
        payload.rule_id = editingItem.rule_id;
      }
      const res = await fetch('/api/v1/banquet-crm/scoring-rules', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!res.ok) {
        const { error: apiError } = await parseJson(res);
        throw new Error(apiError || 'Failed to save rule');
      }
      setMessage(editingItem ? 'Rule updated' : 'Rule created');
      setShowRuleModal(false);
      setEditingItem(null);
      loadScoringRules();
    } catch (err) {
      setError(err?.message || 'Failed to save rule');
    }
  };

  const openEditStage = (stage) => {
    setEditingItem(stage);
    setStageForm({
      stage_key: stage.stage_key,
      stage_name: stage.stage_name,
      stage_color: stage.stage_color || '#3b82f6',
      sort_order: stage.sort_order || 0,
      is_active: stage.is_active,
      sla_hours: stage.sla_hours || 0,
      is_terminal: stage.is_terminal || false,
      auto_action: stage.auto_action || '',
      crm_stage_id: stage.crm_stage_id || ''
    });
    setShowStageModal(true);
  };

  const openEditReason = (reason) => {
    setEditingItem(reason);
    setReasonForm({
      reason_key: reason.reason_key,
      reason_label: reason.reason_label,
      is_active: reason.is_active
    });
    setShowReasonModal(true);
  };

  const openEditRule = (rule) => {
    setEditingItem(rule);
    setRuleForm({
      score_dimension: rule.score_dimension || '',
      rule_field: rule.rule_field || '',
      rule_operator: rule.rule_operator || '',
      rule_value: rule.rule_value || '',
      score_points: rule.score_points || 0,
      is_active: rule.is_active
    });
    setShowRuleModal(true);
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold text-gray-800 mb-4">CRM Configuration</h2>

      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}
      {message && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded">{message}</div>}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow mb-4">
        <div className="border-b flex">
          {['config', 'stages', 'reasons', 'scoring', 'integration', 'easecrm-property', 'push-log'].map(tab => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); if (tab === 'push-log') loadPushLog(); }}
              className={`px-6 py-3 text-sm font-medium ${activeTab === tab ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              {tab === 'config' ? 'General Settings' : tab === 'stages' ? 'Pipeline Stages' : tab === 'reasons' ? 'Lost Reasons' : tab === 'scoring' ? 'Scoring Rules' : tab === 'integration' ? 'EaseCRM (Legacy HMAC)' : tab === 'easecrm-property' ? 'EaseCRM Per-Property' : 'EaseCRM Push Log'}
            </button>
          ))}
        </div>

        <div className="p-6">
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : (
            <>
              {/* General Config */}
              {activeTab === 'config' && (
                <div className="space-y-6 max-w-xl">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium">Auto Assign Enabled</label>
                      <p className="text-sm text-gray-500">Automatically assign new leads to sales reps</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={configForm.auto_assign_enabled}
                      onChange={(e) => setConfigForm({ ...configForm, auto_assign_enabled: e.target.checked })}
                      className="w-5 h-5"
                    />
                  </div>

                  {configForm.auto_assign_enabled && (
                    <div>
                      <label className="block font-medium mb-1">Auto Assign User IDs</label>
                      <input
                        type="text"
                        value={configForm.auto_assign_user_ids}
                        onChange={(e) => setConfigForm({ ...configForm, auto_assign_user_ids: e.target.value })}
                        className="w-full px-3 py-2 border rounded"
                        placeholder="Comma-separated user IDs for auto-assignment"
                      />
                      <p className="text-sm text-gray-500 mt-1">User IDs to include in the auto-assignment rotation</p>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <label className="font-medium">Auto Acknowledge Enabled</label>
                      <p className="text-sm text-gray-500">Automatically acknowledge new leads upon receipt</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={configForm.auto_acknowledge_enabled}
                      onChange={(e) => setConfigForm({ ...configForm, auto_acknowledge_enabled: e.target.checked })}
                      className="w-5 h-5"
                    />
                  </div>

                  <div>
                    <label className="block font-medium mb-1">SLA: New Lead Response (hours)</label>
                    <input
                      type="number"
                      value={configForm.sla_new_lead_hours}
                      onChange={(e) => setConfigForm({ ...configForm, sla_new_lead_hours: parseInt(e.target.value) || 4 })}
                      className="w-full px-3 py-2 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-1">Maximum hours to respond to a new lead before SLA breach</p>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">SLA: Proposal Turnaround (hours)</label>
                    <input
                      type="number"
                      value={configForm.sla_proposal_hours}
                      onChange={(e) => setConfigForm({ ...configForm, sla_proposal_hours: parseInt(e.target.value) || 72 })}
                      className="w-full px-3 py-2 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-1">Maximum hours to send a proposal after lead qualification</p>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Stale Lead Threshold (days)</label>
                    <input
                      type="number"
                      value={configForm.stale_lead_days}
                      onChange={(e) => setConfigForm({ ...configForm, stale_lead_days: parseInt(e.target.value) || 14 })}
                      className="w-full px-3 py-2 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-1">Lead becomes stale if no activity for this many days</p>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Tentative Hold Period (days)</label>
                    <input
                      type="number"
                      value={configForm.tentative_hold_days}
                      onChange={(e) => setConfigForm({ ...configForm, tentative_hold_days: parseInt(e.target.value) || 7 })}
                      className="w-full px-3 py-2 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-1">Number of days a tentative booking hold remains valid</p>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Scoring Weights (JSON)</label>
                    <input
                      type="text"
                      value={configForm.scoring_weights}
                      onChange={(e) => setConfigForm({ ...configForm, scoring_weights: e.target.value })}
                      className="w-full px-3 py-2 border rounded font-mono text-sm"
                      placeholder='{"conversion":40,"revenue":35,"urgency":25}'
                    />
                    <p className="text-sm text-gray-500 mt-1">JSON object with scoring dimension weights</p>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Bucket Thresholds (JSON)</label>
                    <input
                      type="text"
                      value={configForm.bucket_thresholds}
                      onChange={(e) => setConfigForm({ ...configForm, bucket_thresholds: e.target.value })}
                      className="w-full px-3 py-2 border rounded font-mono text-sm"
                      placeholder='{"VIP":80,"HOT":60,"WARM":40,"COLD":20}'
                    />
                    <p className="text-sm text-gray-500 mt-1">JSON object with score thresholds for lead buckets</p>
                  </div>

                  <div>
                    <label className="block font-medium mb-1">Feedback Auto-Send (days)</label>
                    <input
                      type="number"
                      value={configForm.feedback_auto_send_days}
                      onChange={(e) => setConfigForm({ ...configForm, feedback_auto_send_days: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border rounded"
                    />
                    <p className="text-sm text-gray-500 mt-1">Days after event to auto-send feedback request</p>
                  </div>

                  <button
                    onClick={saveConfig}
                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Save Settings
                  </button>
                </div>
              )}

              {/* Pipeline Stages */}
              {activeTab === 'stages' && (
                <div>
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setStageForm({ stage_key: '', stage_name: '', stage_color: '#3b82f6', sort_order: pipelineStages.length, is_active: true, sla_hours: 0, is_terminal: false, auto_action: '', crm_stage_id: '' });
                        setShowStageModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      + Add Stage
                    </button>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Order</th>
                        <th className="text-left py-2 px-3">Key</th>
                        <th className="text-left py-2 px-3">Name</th>
                        <th className="text-left py-2 px-3">Color</th>
                        <th className="text-left py-2 px-3">SLA Hours</th>
                        <th className="text-left py-2 px-3">Terminal</th>
                        <th className="text-left py-2 px-3">CRM Stage ID</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pipelineStages.map(stage => (
                        <tr key={stage.stage_id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3">{stage.sort_order}</td>
                          <td className="py-2 px-3 font-mono text-sm">{stage.stage_key}</td>
                          <td className="py-2 px-3">{stage.stage_name}</td>
                          <td className="py-2 px-3">
                            <div className="w-6 h-6 rounded" style={{ backgroundColor: stage.stage_color || '#ccc' }}></div>
                          </td>
                          <td className="py-2 px-3">{stage.sla_hours || '-'}</td>
                          <td className="py-2 px-3">
                            {stage.is_terminal ? (
                              <span className="text-xs px-2 py-1 rounded bg-purple-100 text-purple-700">Yes</span>
                            ) : (
                              <span className="text-xs text-gray-400">No</span>
                            )}
                          </td>
                          <td className="py-2 px-3 font-mono text-sm">
                            {stage.crm_stage_id ? <span className="text-gray-700">{stage.crm_stage_id}</span> : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-2 py-1 rounded ${stage.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {stage.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <button onClick={() => openEditStage(stage)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Lost Reasons */}
              {activeTab === 'reasons' && (
                <div>
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setReasonForm({ reason_key: '', reason_label: '', is_active: true });
                        setShowReasonModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      + Add Reason
                    </button>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Key</th>
                        <th className="text-left py-2 px-3">Label</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lostReasons.map(reason => (
                        <tr key={reason.reason_id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-mono text-sm">{reason.reason_key}</td>
                          <td className="py-2 px-3">{reason.reason_label}</td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-2 py-1 rounded ${reason.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {reason.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <button onClick={() => openEditReason(reason)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Scoring Rules */}
              {activeTab === 'scoring' && (
                <div>
                  <div className="flex justify-end mb-4">
                    <button
                      onClick={() => {
                        setEditingItem(null);
                        setRuleForm({ score_dimension: '', rule_field: '', rule_operator: '', rule_value: '', score_points: 0, is_active: true });
                        setShowRuleModal(true);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      + Add Rule
                    </button>
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Dimension</th>
                        <th className="text-left py-2 px-3">Field</th>
                        <th className="text-left py-2 px-3">Operator</th>
                        <th className="text-left py-2 px-3">Value</th>
                        <th className="text-left py-2 px-3">Points</th>
                        <th className="text-left py-2 px-3">Status</th>
                        <th className="text-left py-2 px-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {scoringRules.map(rule => (
                        <tr key={rule.rule_id} className="border-b hover:bg-gray-50">
                          <td className="py-2 px-3 font-mono text-sm">{rule.score_dimension}</td>
                          <td className="py-2 px-3">{rule.rule_field}</td>
                          <td className="py-2 px-3">{rule.rule_operator}</td>
                          <td className="py-2 px-3">{rule.rule_value || '-'}</td>
                          <td className="py-2 px-3 font-medium">{rule.score_points > 0 ? `+${rule.score_points}` : rule.score_points}</td>
                          <td className="py-2 px-3">
                            <span className={`text-xs px-2 py-1 rounded ${rule.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                              {rule.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="py-2 px-3">
                            <button onClick={() => openEditRule(rule)} className="text-blue-600 hover:text-blue-800 text-sm">Edit</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* EaseCRM Integration Tab */}
              {activeTab === 'integration' && (
                <div className="max-w-2xl">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">EaseCRM Integration <span className="ml-2 text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded">DEPRECATED</span></h3>
                  <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-sm text-amber-900">
                    <b>This tab is no longer used.</b> EaseCRM has switched from HMAC-signed requests to bearer-token authentication, so the "Test Outbound" button below will always fail with <code>Bearer Token Missing</code>. The live integration runs from the <b>EaseCRM Per-Property</b> tab instead — configure URLs, bearer tokens, and default Source / Sub-Source there. The inbound webhook URL listed below is still valid (EaseCRM still POSTs new leads to it), but stage-update push has moved.
                  </div>
                  <p className="text-sm text-gray-500 mb-4">
                    (Legacy fields kept read-only-ish for reference. Inbound side still works for callbacks signed with the old shared secret.)
                  </p>

                  {crmError && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded text-sm">{crmError}</div>}
                  {crmMessage && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded text-sm">{crmMessage}</div>}

                  {/* Inbound Webhook URL */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Inbound Lead Webhook URL <span className="font-normal text-gray-400">(share with EaseCRM team)</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        readOnly
                        value={crmLeadCallbackUrl}
                        className="flex-1 px-3 py-2 border rounded bg-gray-50 text-blue-700 font-mono text-sm"
                      />
                      <button
                        onClick={handleCopyCrmUrl}
                        className={`px-4 py-2 text-white rounded text-sm ${crmUrlCopied ? 'bg-green-600' : 'bg-blue-600 hover:bg-blue-700'}`}
                      >
                        {crmUrlCopied ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      EaseCRM must POST JSON with headers <code className="bg-gray-100 px-1 rounded">X-EaseCRM-Signature</code> (HMAC-SHA256 hex) and <code className="bg-gray-100 px-1 rounded">X-EaseCRM-Timestamp</code> (Unix ms, ±5min).
                    </p>
                  </div>

                  {/* Outbound: CRM Status Update URL */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      CRM Status Update URL <span className="font-normal text-gray-400">(EaseCRM endpoint for stage change push)</span>
                    </label>
                    <input
                      value={crmStatusUrl}
                      onChange={(e) => setCrmStatusUrl(e.target.value.slice(0, 500))}
                      placeholder="https://easecrm.easeroom.co/api/leads/stage-update"
                      maxLength={500}
                      className="w-full px-3 py-2 border rounded text-sm"
                    />
                    <span className="text-xs text-gray-400">{crmStatusUrl.length}/500</span>
                  </div>

                  {/* Shared HMAC Secret */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Shared HMAC Secret <span className="font-normal text-gray-400">(used for both inbound + outbound signing)</span>
                    </label>
                    {crmSecretSet && !crmSecret ? (
                      <div className="flex gap-2 items-center">
                        <input
                          readOnly
                          value={crmSecretMasked}
                          className="flex-1 px-3 py-2 border rounded bg-gray-50 text-gray-600 font-mono text-sm"
                        />
                        <button
                          onClick={handleGenerateCrmSecret}
                          className="px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                          title="Generate a new secret and replace the current one"
                        >
                          Rotate Secret
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2 items-center">
                        <input
                          type={crmSecretVisible ? 'text' : 'password'}
                          value={crmSecret}
                          onChange={(e) => setCrmSecret(e.target.value.slice(0, 200))}
                          placeholder={crmSecretSet ? 'New secret…' : 'Enter or generate a secret'}
                          maxLength={200}
                          className="flex-1 px-3 py-2 border rounded font-mono text-sm"
                        />
                        <button
                          onClick={() => setCrmSecretVisible(!crmSecretVisible)}
                          className="px-3 py-2 bg-gray-500 text-white rounded text-sm"
                        >
                          {crmSecretVisible ? 'Hide' : 'Show'}
                        </button>
                        <button
                          onClick={handleGenerateCrmSecret}
                          className="px-4 py-2 bg-purple-600 text-white rounded text-sm hover:bg-purple-700"
                        >
                          Generate
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-gray-400 mt-1">
                      Secret is stored server-side (only last 4 chars shown). Both EASEPMS and EaseCRM must share the same value.
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={handleSaveCrm}
                      disabled={crmSaving}
                      className="px-5 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      {crmSaving ? 'Saving…' : 'Save EaseCRM Settings'}
                    </button>
                    <button
                      onClick={handleTestCrmConnection}
                      disabled={crmTesting || !crmSecretSet || !crmStatusUrl}
                      className="px-5 py-2 bg-cyan-600 text-white rounded text-sm hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!crmSecretSet || !crmStatusUrl ? 'Save URL + secret first' : 'Send a test PING to the CRM Status Update URL'}
                    >
                      {crmTesting ? 'Testing…' : 'Test Outbound'}
                    </button>
                  </div>

                  {/* Test Result */}
                  {crmTestResult && (
                    <div className={`mt-4 p-3 rounded text-sm ${crmTestResult.success ? 'bg-green-50 border border-green-300 text-green-800' : 'bg-red-50 border border-red-300 text-red-800'}`}>
                      <div className="font-semibold mb-1">
                        {crmTestResult.success ? 'Test succeeded' : 'Test failed'}
                        {typeof crmTestResult.http_status === 'number' && crmTestResult.http_status > 0 && (
                          <span className="font-normal ml-2">HTTP {crmTestResult.http_status}</span>
                        )}
                        {typeof crmTestResult.elapsed_ms === 'number' && (
                          <span className="font-normal ml-2">{crmTestResult.elapsed_ms}ms</span>
                        )}
                      </div>
                      {crmTestResult.error && <div className="font-mono text-xs">Error: {crmTestResult.error}</div>}
                      {crmTestResult.response_body && (
                        <details className="mt-1">
                          <summary className="cursor-pointer text-xs">Response body</summary>
                          <pre className="mt-1 text-xs whitespace-pre-wrap break-all">{crmTestResult.response_body}</pre>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ── EaseCRM Per-Property (Bearer-token integration) ── */}
              {activeTab === 'easecrm-property' && (
                <div className="max-w-2xl">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">EaseCRM Per-Property Integration</h3>
                  <p className="text-sm text-gray-500 mb-4">
                    Each hotel uses its own EaseCRM bearer tokens. Configure the URLs and tokens, then pick the default Source / Sub-Source the property sends with every new lead push.
                  </p>

                  {ecrmError && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded text-sm">{ecrmError}</div>}
                  {ecrmMessage && <div className="mb-3 p-3 bg-green-100 text-green-700 rounded text-sm">{ecrmMessage}</div>}

                  {/* Enabled toggle */}
                  <div className="mb-4 flex items-center gap-2">
                    <input id="ecrmEnabled" type="checkbox" checked={!!ecrmCfg.is_enabled}
                      onChange={(e) => setEcrmCfg({ ...ecrmCfg, is_enabled: e.target.checked })} />
                    <label htmlFor="ecrmEnabled" className="text-sm">Push leads + stage updates to EaseCRM for this property</label>
                  </div>

                  {/* Lead Push (Create) */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Lead Push URL</label>
                    <input value={ecrmCfg.lead_push_url}
                      onChange={(e) => setEcrmCfg({ ...ecrmCfg, lead_push_url: e.target.value.slice(0, 500) })}
                      placeholder="https://easecrm.easeroom.co/api/lead_push"
                      maxLength={500}
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Lead Push Bearer Token
                      {ecrmCfg.lead_push_token_set && !ecrmCfg.lead_push_token && (
                        <span className="font-normal text-gray-500 ml-2">currently set: {ecrmCfg.lead_push_token_masked}</span>
                      )}
                    </label>
                    <input type="password"
                      value={ecrmCfg.lead_push_token}
                      onChange={(e) => setEcrmCfg({ ...ecrmCfg, lead_push_token: e.target.value.slice(0, 500) })}
                      placeholder={ecrmCfg.lead_push_token_set ? 'Enter to replace…' : 'Paste bearer token'}
                      maxLength={500}
                      className="w-full px-3 py-2 border rounded font-mono text-sm" />
                    <p className="text-xs text-gray-400 mt-1">Leave blank to keep the saved token.</p>
                  </div>

                  {/* Stage Update */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Lead Stage Update URL</label>
                    <input value={ecrmCfg.lead_stage_update_url}
                      onChange={(e) => setEcrmCfg({ ...ecrmCfg, lead_stage_update_url: e.target.value.slice(0, 500) })}
                      placeholder="https://easecrm.easeroom.co/api/lead_stage_update"
                      maxLength={500}
                      className="w-full px-3 py-2 border rounded text-sm" />
                  </div>
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Lead Stage Update Bearer Token
                      {ecrmCfg.lead_stage_update_token_set && !ecrmCfg.lead_stage_update_token && (
                        <span className="font-normal text-gray-500 ml-2">currently set: {ecrmCfg.lead_stage_update_token_masked}</span>
                      )}
                    </label>
                    <input type="password"
                      value={ecrmCfg.lead_stage_update_token}
                      onChange={(e) => setEcrmCfg({ ...ecrmCfg, lead_stage_update_token: e.target.value.slice(0, 500) })}
                      placeholder={ecrmCfg.lead_stage_update_token_set ? 'Enter to replace…' : 'Paste bearer token'}
                      maxLength={500}
                      className="w-full px-3 py-2 border rounded font-mono text-sm" />
                    <p className="text-xs text-gray-400 mt-1">Leave blank to keep the saved token.</p>
                  </div>

                  <div className="border-t my-4"></div>

                  {/* Source / Sub-Source dropdowns (loaded via proxy → EaseCRM) */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Default Source</label>
                    <div className="flex gap-2 items-center">
                      <select className="flex-1 px-3 py-2 border rounded text-sm"
                        value={ecrmCfg.default_source_id}
                        onChange={(e) => {
                          const id = e.target.value;
                          const match = ecrmSources.find((s) => String(s.id ?? s.source_id ?? s.value) === String(id));
                          const name = match ? String(match.name ?? match.source_name ?? match.label ?? '') : '';
                          setEcrmCfg({ ...ecrmCfg, default_source_id: id, default_source_name: name, default_sub_source_id: '', default_sub_source_name: '' });
                          setEcrmSubSources([]);
                          if (id) fetchEcrmSubSources(id);
                        }}
                      >
                        <option value="">{ecrmCfg.default_source_name ? `${ecrmCfg.default_source_name} (currently saved)` : '— Select Source —'}</option>
                        {ecrmSources.map((s) => {
                          const sid = String(s.id ?? s.source_id ?? s.value ?? '');
                          const sname = String(s.name ?? s.source_name ?? s.label ?? sid);
                          return <option key={sid} value={sid}>{sname}</option>;
                        })}
                      </select>
                      <button onClick={fetchEcrmSources} disabled={ecrmLoadingSources || !ecrmCfg.lead_push_token_set}
                        className="px-3 py-2 bg-gray-600 text-white rounded text-sm disabled:opacity-50"
                        title={!ecrmCfg.lead_push_token_set ? 'Save the lead-push token first' : 'Refresh sources from EaseCRM'}>
                        {ecrmLoadingSources ? 'Loading…' : 'Load'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Default Sub-Source</label>
                    <div className="flex gap-2 items-center">
                      <select className="flex-1 px-3 py-2 border rounded text-sm"
                        value={ecrmCfg.default_sub_source_id}
                        onChange={(e) => {
                          const id = e.target.value;
                          const match = ecrmSubSources.find((s) => String(s.id ?? s.sub_source_id ?? s.value) === String(id));
                          const name = match ? String(match.name ?? match.sub_source_name ?? match.label ?? '') : '';
                          setEcrmCfg({ ...ecrmCfg, default_sub_source_id: id, default_sub_source_name: name });
                        }}
                        disabled={!ecrmCfg.default_source_id}>
                        <option value="">{ecrmCfg.default_sub_source_name ? `${ecrmCfg.default_sub_source_name} (currently saved)` : '— Select Sub-Source —'}</option>
                        {ecrmSubSources.map((s) => {
                          const sid = String(s.id ?? s.sub_source_id ?? s.value ?? '');
                          const sname = String(s.name ?? s.sub_source_name ?? s.label ?? sid);
                          return <option key={sid} value={sid}>{sname}</option>;
                        })}
                      </select>
                      <button onClick={() => fetchEcrmSubSources(ecrmCfg.default_source_id)}
                        disabled={ecrmLoadingSubSources || !ecrmCfg.default_source_id}
                        className="px-3 py-2 bg-gray-600 text-white rounded text-sm disabled:opacity-50">
                        {ecrmLoadingSubSources ? 'Loading…' : 'Load'}
                      </button>
                    </div>
                  </div>

                  <div className="flex gap-3 mt-4">
                    <button onClick={saveEcrmConfig} disabled={ecrmSaving}
                      className="px-5 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                      {ecrmSaving ? 'Saving…' : 'Save EaseCRM Settings'}
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mt-4">
                    Tip: also set each pipeline stage's <b>EaseCRM Stage ID</b> on the Pipeline Stages tab so stage changes can be pushed back to EaseCRM.
                  </p>
                </div>
              )}

              {/* ── EaseCRM Push Log ── */}
              {activeTab === 'push-log' && (
                <div>
                  <div className="flex flex-wrap items-end gap-3 mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-1">EaseCRM Push Log</h3>
                      <p className="text-sm text-gray-500">Most recent 50 outbound push attempts for this property. Refresh after creating a lead or changing a stage to see the result.</p>
                    </div>
                    <div className="ml-auto flex gap-2 items-end">
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Lead No</label>
                        <input value={pushLogFilterLead} onChange={(e) => setPushLogFilterLead(e.target.value)}
                          placeholder="BL-202604-…" className="px-2 py-1 border rounded text-sm" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-0.5">Outcome contains</label>
                        <input value={pushLogFilterOutcome} onChange={(e) => setPushLogFilterOutcome(e.target.value)}
                          placeholder="OK, ERROR, SKIPPED…" className="px-2 py-1 border rounded text-sm" />
                      </div>
                      <button onClick={loadPushLog} disabled={pushLogLoading}
                        className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
                        {pushLogLoading ? 'Loading…' : '↻ Refresh'}
                      </button>
                    </div>
                  </div>

                  {pushLogError && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded text-sm">{pushLogError}</div>}

                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-2">When</th>
                        <th className="text-left py-2 px-2">Outcome</th>
                        <th className="text-left py-2 px-2">HTTP</th>
                        <th className="text-left py-2 px-2">Lead</th>
                        <th className="text-left py-2 px-2">EaseCRM Id</th>
                        <th className="text-left py-2 px-2">Stage</th>
                        <th className="text-left py-2 px-2">Detail</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pushLog.length === 0 ? (
                        <tr><td colSpan={7} className="py-6 text-center text-gray-400">{pushLogLoading ? 'Loading…' : 'No rows yet — create a lead or change a stage, then click Refresh.'}</td></tr>
                      ) : pushLog.map((r, i) => {
                        const ok = /_OK$/.test(String(r.outcome || ''));
                        const skipped = /SKIPPED/.test(String(r.outcome || ''));
                        const err = /ERROR/.test(String(r.outcome || ''));
                        const badge = ok ? 'bg-green-100 text-green-800'
                          : skipped ? 'bg-amber-100 text-amber-800'
                          : err ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700';
                        const stageText = r.from_stage || r.to_stage
                          ? `${r.from_stage || '–'} → ${r.to_stage || '–'}`
                          : (r.stage || '–');
                        return (
                          <tr key={i} className="border-b hover:bg-gray-50 align-top">
                            <td className="py-2 px-2 text-xs text-gray-600 whitespace-nowrap">{r.received_at ? new Date(r.received_at).toLocaleString() : '–'}</td>
                            <td className="py-2 px-2"><span className={`inline-block px-2 py-0.5 rounded text-xs font-mono ${badge}`}>{r.outcome || '–'}</span></td>
                            <td className="py-2 px-2 font-mono text-xs">{r.http_status || '–'}</td>
                            <td className="py-2 px-2 font-mono text-xs">{r.lead_no || '–'}</td>
                            <td className="py-2 px-2 font-mono text-xs">{r.easecrm_lead_id || '–'}</td>
                            <td className="py-2 px-2 text-xs">{stageText}</td>
                            <td className="py-2 px-2 text-xs text-gray-600">{r.error_message || ''}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Stage Modal */}
      {showStageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{editingItem ? 'Edit Stage' : 'Add Stage'}</h3>
              <button onClick={() => setShowStageModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Stage Key *</label>
                <input
                  type="text"
                  value={stageForm.stage_key}
                  onChange={(e) => setStageForm({ ...stageForm, stage_key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                  disabled={!!editingItem}
                  className="w-full px-3 py-2 border rounded disabled:bg-gray-100"
                  placeholder="e.g., FOLLOW_UP"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Stage Name *</label>
                <input
                  type="text"
                  value={stageForm.stage_name}
                  onChange={(e) => setStageForm({ ...stageForm, stage_name: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="e.g., Follow Up"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Color</label>
                  <input
                    type="color"
                    value={stageForm.stage_color}
                    onChange={(e) => setStageForm({ ...stageForm, stage_color: e.target.value })}
                    className="w-full h-10 border rounded"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Sort Order</label>
                  <input
                    type="number"
                    value={stageForm.sort_order}
                    onChange={(e) => setStageForm({ ...stageForm, sort_order: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border rounded"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">SLA Hours</label>
                <input
                  type="number"
                  value={stageForm.sla_hours}
                  onChange={(e) => setStageForm({ ...stageForm, sla_hours: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded"
                  min="0"
                  placeholder="0 = no SLA"
                />
                <p className="text-xs text-gray-500 mt-1">How long a lead can stay in this stage before SLA breach. 0 = no SLA.</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Auto Action on Entry</label>
                <select
                  value={stageForm.auto_action}
                  onChange={(e) => setStageForm({ ...stageForm, auto_action: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">None</option>
                  <option value="CREATE_QUOTATION">Create Quotation</option>
                  <option value="CREATE_BOOKING">Create Booking</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Action triggered when a lead enters this stage</p>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">EaseCRM Stage ID</label>
                <input
                  type="text"
                  value={stageForm.crm_stage_id}
                  onChange={(e) => setStageForm({ ...stageForm, crm_stage_id: e.target.value.replace(/[^A-Za-z0-9_-]/g, '').slice(0, 40) })}
                  className="w-full px-3 py-2 border rounded font-mono"
                  placeholder="e.g. 2"
                />
                <p className="text-xs text-gray-500 mt-1">stage_id this PMS stage maps to in EaseCRM. Leave blank to skip pushing this stage.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="stage_terminal"
                  checked={stageForm.is_terminal}
                  onChange={(e) => setStageForm({ ...stageForm, is_terminal: e.target.checked })}
                />
                <label htmlFor="stage_terminal" className="text-sm">Terminal Stage</label>
                <span className="text-xs text-gray-500">(leads in this stage are considered closed)</span>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="stage_active"
                  checked={stageForm.is_active}
                  onChange={(e) => setStageForm({ ...stageForm, is_active: e.target.checked })}
                />
                <label htmlFor="stage_active" className="text-sm">Active</label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowStageModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={saveStage} className="px-4 py-2 bg-blue-600 text-white rounded">{editingItem ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Reason Modal */}
      {showReasonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{editingItem ? 'Edit Reason' : 'Add Reason'}</h3>
              <button onClick={() => setShowReasonModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Reason Key *</label>
                <input
                  type="text"
                  value={reasonForm.reason_key}
                  onChange={(e) => setReasonForm({ ...reasonForm, reason_key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
                  disabled={!!editingItem}
                  className="w-full px-3 py-2 border rounded disabled:bg-gray-100"
                  placeholder="e.g., BUDGET_MISMATCH"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Reason Label *</label>
                <input
                  type="text"
                  value={reasonForm.reason_label}
                  onChange={(e) => setReasonForm({ ...reasonForm, reason_label: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="e.g., Budget Mismatch"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="reason_active"
                  checked={reasonForm.is_active}
                  onChange={(e) => setReasonForm({ ...reasonForm, is_active: e.target.checked })}
                />
                <label htmlFor="reason_active" className="text-sm">Active</label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowReasonModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={saveLostReason} className="px-4 py-2 bg-blue-600 text-white rounded">{editingItem ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Rule Modal */}
      {showRuleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="p-4 border-b flex justify-between items-center">
              <h3 className="text-lg font-bold">{editingItem ? 'Edit Scoring Rule' : 'Add Scoring Rule'}</h3>
              <button onClick={() => setShowRuleModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Score Dimension *</label>
                <select
                  value={ruleForm.score_dimension}
                  onChange={(e) => setRuleForm({ ...ruleForm, score_dimension: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Select...</option>
                  <option value="conversion">Conversion</option>
                  <option value="revenue">Revenue</option>
                  <option value="urgency">Urgency</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rule Field *</label>
                <input
                  type="text"
                  value={ruleForm.rule_field}
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_field: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="e.g., estimated_budget, event_type, pax_count"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rule Operator *</label>
                <select
                  value={ruleForm.rule_operator}
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_operator: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">Select...</option>
                  <option value="gt">Greater Than (&gt;)</option>
                  <option value="gte">Greater Than or Equal (&gt;=)</option>
                  <option value="lt">Less Than (&lt;)</option>
                  <option value="lte">Less Than or Equal (&lt;=)</option>
                  <option value="eq">Equal (=)</option>
                  <option value="neq">Not Equal (!=)</option>
                  <option value="in">In List</option>
                  <option value="between">Between</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Rule Value</label>
                <input
                  type="text"
                  value={ruleForm.rule_value}
                  onChange={(e) => setRuleForm({ ...ruleForm, rule_value: e.target.value })}
                  className="w-full px-3 py-2 border rounded"
                  placeholder="e.g., 500000 or WEDDING"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Score Points</label>
                <input
                  type="number"
                  value={ruleForm.score_points}
                  onChange={(e) => setRuleForm({ ...ruleForm, score_points: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="rule_active"
                  checked={ruleForm.is_active}
                  onChange={(e) => setRuleForm({ ...ruleForm, is_active: e.target.checked })}
                />
                <label htmlFor="rule_active" className="text-sm">Active</label>
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-3">
              <button onClick={() => setShowRuleModal(false)} className="px-4 py-2 border rounded">Cancel</button>
              <button onClick={saveScoringRule} className="px-4 py-2 bg-blue-600 text-white rounded">{editingItem ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BanquetCrmConfig;
